import { useCallback, useMemo, useState } from 'react';

import type {
  MedicalRecordDocumentOption,
  MedicalRecordEncounterOption,
  MedicalRecordsExportSelection,
  MedicalRecordsOutputMode,
} from './types';

const defaultNoteIds = (encounters: MedicalRecordEncounterOption[]) =>
  encounters.flatMap(encounter =>
    encounter.notes.filter(note => note.selectedByDefault !== false).map(note => note.id),
  );

const defaultDocumentIds = (documents: MedicalRecordDocumentOption[]) =>
  documents
    .filter(document => document.eligible !== false)
    .filter(document => document.selectedByDefault !== false)
    .map(document => document.id);

export function useMedicalRecordsExportSelection(
  encounters: MedicalRecordEncounterOption[],
  documents: MedicalRecordDocumentOption[],
  outputMode: MedicalRecordsOutputMode,
) {
  const [selectedNoteIds, setSelectedNoteIds] = useState<Set<string>>(
    () => new Set(defaultNoteIds(encounters)),
  );
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(
    () => new Set(defaultDocumentIds(documents)),
  );

  const toggleNote = useCallback((noteId: string) => {
    setSelectedNoteIds(current => {
      const next = new Set(current);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  }, []);

  const toggleEncounter = useCallback(
    (encounterId: string) => {
      const encounter = encounters.find(item => item.id === encounterId);
      if (!encounter) return;

      setSelectedNoteIds(current => {
        const next = new Set(current);
        const hasEveryNote = encounter.notes.every(note => next.has(note.id));
        for (const note of encounter.notes) {
          if (hasEveryNote) next.delete(note.id);
          else next.add(note.id);
        }
        return next;
      });
    },
    [encounters],
  );

  const toggleDocument = useCallback(
    (documentId: string) => {
      const document = documents.find(item => item.id === documentId);
      if (document?.eligible === false) return;
      setSelectedDocumentIds(current => {
        const next = new Set(current);
        if (next.has(documentId)) next.delete(documentId);
        else next.add(documentId);
        return next;
      });
    },
    [documents],
  );

  const selectedEncounters = useMemo(
    () =>
      encounters.filter(encounter => encounter.notes.some(note => selectedNoteIds.has(note.id))),
    [encounters, selectedNoteIds],
  );

  const selectedNotes = useMemo(
    () =>
      encounters.flatMap(encounter => encounter.notes.filter(note => selectedNoteIds.has(note.id))),
    [encounters, selectedNoteIds],
  );

  const selectedDocuments = useMemo(
    () => documents.filter(document => selectedDocumentIds.has(document.id)),
    [documents, selectedDocumentIds],
  );

  const generatedNoteCount = selectedNotes.filter(note => note.pdfSource === 'generated').length;
  const pageCount = [...selectedNotes, ...selectedDocuments].reduce(
    (total, item) => total + item.pageCount,
    0,
  );
  const fileCount = selectedNotes.length + selectedDocuments.length;

  const selection: MedicalRecordsExportSelection = {
    encounterIds: selectedEncounters.map(encounter => encounter.id),
    noteIds: selectedNotes.map(note => note.id),
    documentReferenceIds: selectedDocuments.map(document => document.id),
    outputMode,
  };

  return {
    fileCount,
    generatedNoteCount,
    pageCount,
    selectedDocumentIds,
    selectedDocuments,
    selectedEncounterCount: selectedEncounters.length,
    selectedNoteIds,
    selectedNotes,
    selection,
    toggleDocument,
    toggleEncounter,
    toggleNote,
  };
}
